<script setup lang="ts">
import DataTable from 'primevue/datatable'
import { computed, onMounted, ref } from 'vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import Button from 'primevue/button'
import { Icon } from '@iconify/vue/dist/iconify.js'
import Column from 'primevue/column'
import { formatedDate, generateUniqueId } from '@/constants/index'
import InputText from 'primevue/inputtext'
import Calendar from 'primevue/calendar'
import { useRouter } from 'vue-router'
import { addMergeWastageToMerge, getYarnById, updateMergeWastage, deleteMergeWastage } from '@/api/yarns'
import { useMergeForm } from '@/composables/useMergeForm'
import type { Wastage, WastageRequest } from '@/types/yarn-types'
import type { Batch } from '@/types/batch-types'
import { getBatchList } from '@/api/batches'
import {getProductList} from "@/api/products";
import type {Product} from "@/types/product-types";

const searchQuery = ref('')
const editingRow = ref<string | null>(null)
const isEditingRow = (rowId: string) => editingRow.value === rowId

const router = useRouter()

const wastages = ref<Wastage[]>([])
const { loading, showToast } = useMergeForm('view')
const batches = ref<Batch[] | undefined>()
const yarnId = router.currentRoute.value.params.id as string
const yarn = ref<any>()
const isAddingNew = ref<boolean>(false)
const qualities = ref<Product[]>([])

const originalValues = ref<{[key: string]: any}>({})

const getBatchNumber = (batchId: string | null | undefined) => {
  if (!batchId) return 'Not linked to batch'
  return batches.value?.find((batch) => batch.id == batchId)?.batchNumber || 'Not linked to batch'
}

const getQualityName = (qualityId: string | null | undefined) => {
  if (!qualityId) return 'Not linked to quality'
  const quality = qualities.value.find((quality: Product) => quality.id === qualityId)
  return quality?.unfinishedReference || 'Not linked to quality'
}

const isFromBatch = (wastage: Wastage) => {
  return wastage.batchId && true
}

const canEditWastage = (wastage: Wastage) => {
  return !isFromBatch(wastage) && !wastage.isNew
}

const canDeleteWastage = (wastage: Wastage) => {
  return !isFromBatch(wastage) && !wastage.isNew
}

const filteredUsages = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return wastages.value.filter(
      (wastage: Wastage) =>
          String(wastage.kiloOfYarnWasted)?.includes(query) ||
          String(wastage.percentageWasted)?.includes(query) ||
          formatedDate(wastage.dateRecorded.toString())?.includes(query) ||
          getBatchNumber(wastage.batchId)?.toLowerCase().includes(query) ||
          getQualityName(wastage.knittingQualityId)?.toLowerCase().includes(query)
  )
})

const addWastage = () => {
  const id = generateUniqueId()

  const newRow: Wastage = {
    id,
    kiloOfYarnWasted: '',
    dateRecorded: new Date(),
    percentageWasted: 0,
    batchId: null,
    knittingQualityId: null,
    isNew: true
  }
  wastages.value.push(newRow)
  handleEnableEdit(id)
  isAddingNew.value = true
}

const handleEnableEdit = (id: string) => {
  const item = wastages.value.find(w => w.id === id)
  if (item) {
    originalValues.value[id] = {
      kiloOfYarnWasted: item.kiloOfYarnWasted,
      dateRecorded: item.dateRecorded
    }
  }
  editingRow.value = id
}

const handleSave = async (index: number, id: string) => {
  const item = wastages.value.find((item) => item.id == id)

  if (!item?.kiloOfYarnWasted || parseFloat(String(item.kiloOfYarnWasted)) <= 0) {
    showToast('error', 'Invalid Input', 'Please enter a valid amount of yarn wasted.')
    return
  }

  let dateToSave: string;
  if (item.dateRecorded instanceof Date && !isNaN(item.dateRecorded.getTime())) {
    dateToSave = item.dateRecorded.toISOString();
  } else {
    dateToSave = new Date().toISOString();
  }

  try {
    loading.value = true

  const wastageRequest: WastageRequest = {
    kiloOfYarnWasted: Number(item.kiloOfYarnWasted),
    dateRecorded: item.dateRecorded ? new Date(item.dateRecorded).toISOString() : new Date().toISOString()
  }

    await addMergeWastageToMerge(wastageRequest, yarnId)
    showToast('success', 'Wastage Added!', 'You have successfully added a new wastage.')
    await loadWastageData()

    editingRow.value = null
    isAddingNew.value = false
  } catch (error: any) {
    console.error(error)
    wastages.value = wastages.value.filter((item) => item.id != id)
    showToast('error', 'Wastage Not Added!', error.response?.data?.message || 'An error occurred')
  } finally {
    loading.value = false
    delete originalValues.value[id]
  }
}

const handleUpdate = async (index: number, id: string) => {
  const item = wastages.value.find((item) => item.id == id)

  if (!item?.kiloOfYarnWasted || parseFloat(String(item.kiloOfYarnWasted)) <= 0) {
    showToast('error', 'Invalid Input', 'Please enter a valid amount of yarn wasted.')
    return
  }

  let dateToUpdate: string;
  if (item.dateRecorded instanceof Date && !isNaN(item.dateRecorded.getTime())) {
    dateToUpdate = item.dateRecorded.toISOString();
  } else {
    dateToUpdate = new Date().toISOString();
  }

  try {
    loading.value = true

    const updateData = {
      kiloOfYarnWasted: Number(item.kiloOfYarnWasted),
      dateRecorded: dateToUpdate
    }

    await updateMergeWastage(yarnId, id, updateData)
    showToast('success', 'Wastage Updated!', 'You have successfully updated the wastage.')
    await loadWastageData()

    editingRow.value = null
  } catch (error: any) {
    console.error(error)
    showToast('error', 'Wastage Not Updated!', error.response?.data?.message || 'An error occurred')

    if (originalValues.value[id] && item) {
      item.kiloOfYarnWasted = originalValues.value[id].kiloOfYarnWasted
      item.dateRecorded = originalValues.value[id].dateRecorded
    }
  } finally {
    loading.value = false
    delete originalValues.value[id]
  }
}
const handleCancel = (id: string) => {
  if (isAddingNew.value) {
    wastages.value = wastages.value.filter((item) => item.id != id)
    isAddingNew.value = false
  } else {
    const item = wastages.value.find(w => w.id === id)
    if (item && originalValues.value[id]) {
      item.kiloOfYarnWasted = originalValues.value[id].kiloOfYarnWasted
      item.dateRecorded = originalValues.value[id].dateRecorded
    }
  }
  editingRow.value = null
  delete originalValues.value[id]
}

const deleteWastage = async (wastageId: string) => {
  loading.value = true

  await deleteMergeWastage(yarnId, wastageId)
    .then(async () => {
      showToast('success', 'Wastage Deleted!', 'You have successfully deleted the wastage.')
      await loadWastageData()
    })
    .catch((error) => {
      showToast('error', 'Wastage Not Deleted!', error.response?.data?.message || 'Failed to delete wastage')
    })
    .finally(() => {
      loading.value = false
    })
}

const loadWastageData = async () => {
  try {
    yarn.value = await getYarnById(yarnId)
    wastages.value =
        yarn.value?.mergeWastages?.map((item: any) => ({
          ...item,
          dateRecorded: item.dateRecorded ? new Date(item.dateRecorded.toString()) : new Date()
        })) || []
  } catch (error) {
    console.error('Error loading wastage data:', error)
    showToast('error', 'Load Error', 'Failed to load wastage data.')
  }
}

onMounted(async () => {
  loading.value = true
  try {
    batches.value = await getBatchList()
    qualities.value = await getProductList()
    await loadWastageData()
  } catch (error) {
    console.error('Error loading data:', error)
    showToast('error', 'Load Error', 'Failed to load initial data.')
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <DataTable
    :value="filteredUsages"
    paginator
    :rows="10"
    tableStyle="min-width: 50rem"
    stripedRows
    paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
    :rowsPerPageOptions="[10, 25]"
    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} wastages"
    :loading="loading"
    :pt="{
      table: loading && 'min-h-[30rem]'
    }"
    :pt-options="{
      mergeProps: true
    }"
    style="width: 100%"
  >
    <template #empty> <p class="text-center">No wastage found.</p> </template>
    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />
        <TableFilter />
      </div>
    </template>

    <Column field="kiloOfYarnWasted" header="Kilo of Yarn Wasted" sortable style="width: 20%">
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ slotProps.data.kiloOfYarnWasted }} Kg
        </p>
        <InputText
          v-else
          v-model="slotProps.data.kiloOfYarnWasted"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          style="width: 100%"
        />
      </template>
    </Column>

    <Column field="dateRecorded" header="Date Recorded" sortable style="width: 20%">
      <template #body="slotProps">
        <p v-if="!isEditingRow(slotProps.data.id)" class="px-4">
          {{ formatedDate(slotProps.data.dateRecorded) }}
        </p>
        <Calendar
          v-else
          v-model="slotProps.data.dateRecorded"
          :class="isEditingRow(slotProps.data.id) ? '' : 'border-none'"
          placeholder="Enter date recorded"
          style="width: 100%"
          dateFormat="dd/mm/yy"
        />
      </template>
    </Column>

    <Column field="percentageWasted" header="Percentage Wasted" sortable style="width: 15%">
      <template #body="slotProps">
        <div class="px-4">
          <p>
            {{ slotProps.data.percentageWasted ? parseFloat(slotProps.data.percentageWasted).toFixed(2) : '0.00' }}%
          </p>
          <span v-if="isEditingRow(slotProps.data.id)" class="text-xs text-gray-500 block">
            (Auto-calculated)
          </span>
        </div>
      </template>
    </Column>

    <Column field="batchId" header="Batch" sortable style="width: 15%">
      <template #body="slotProps">
        <RouterLink
            v-if="slotProps.data.batchId && !isEditingRow(slotProps.data.id)"
            :to="`/batches/${slotProps.data.batchId}`"
            class="px-4 font-medium text-neutral-800 block hover:underline"
        >
          {{ getBatchNumber(slotProps.data.batchId) }}
        </RouterLink>
        <p v-else-if="!isEditingRow(slotProps.data.id)" class="px-4 text-gray-500">
          {{ getBatchNumber(slotProps.data.batchId) }}
        </p>
        <p v-else class="px-4 text-gray-500 text-sm">
          Not linked to batch
        </p>
      </template>
    </Column>

    <Column field="knittingQualityId" header="Quality" sortable style="width: 15%">
      <template #body="slotProps">
        <RouterLink
            v-if="slotProps.data.knittingQualityId && !isEditingRow(slotProps.data.id)"
            :to="`/productSpecification/${slotProps.data.knittingQualityId}`"
            class="px-4 font-medium text-neutral-800 block hover:underline"
        >
          {{ getQualityName(slotProps.data.knittingQualityId) }}
        </RouterLink>
        <p v-else-if="!isEditingRow(slotProps.data.id)" class="px-4 text-gray-500">
          {{ getQualityName(slotProps.data.knittingQualityId) }}
        </p>
        <p v-else class="px-4 text-gray-500 text-sm">
          Not linked to quality
        </p>
      </template>
    </Column>

    <Column field="" header="Actions" style="width: 15%">
      <template #body="slotProps">
      <div class="flex gap-1">
        <Button
              v-if="!isEditingRow(slotProps.data.id) && canEditWastage(slotProps.data)"
              v-ripple
              outlined
              size="small"
              class="flex-1 gap-x-2"
              @click="handleEnableEdit(slotProps.data.id)"
              :loading="loading"
              :disabled="isAddingNew"
              severity="secondary"
          >
            <span class="text-xs">Edit</span>
            <Icon icon="basil:edit-outline" class="text-lg" />
          </Button>

          <Button
              v-if="!isEditingRow(slotProps.data.id) && canDeleteWastage(slotProps.data)"
              v-ripple
              outlined
              size="small"
              class="flex-1 gap-x-2"
              @click="deleteWastage(slotProps.data.id)"
              :loading="loading"
              :disabled="isAddingNew"
              severity="danger"
          >
            <span class="text-xs">Delete</span>
            <Icon icon="basil:trash-outline" class="text-lg" />
          </Button>

          <template v-else-if="isEditingRow(slotProps.data.id)">
            <Button
                v-if="slotProps.data.isNew"
                v-ripple
                outlined
                size="small"
                class="flex-1 gap-x-2"
            @click="handleSave(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            >
            <span class="text-xs">Save</span>
            <Icon icon="basil:check-outline" class="text-xl" />
          </Button>
          <Button
            v-else
            v-ripple
            outlined
            size="small"
            class="flex-1 gap-x-2"
            @click="handleUpdate(slotProps.index, slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            >
              <span class="text-xs">Update</span>
              <Icon icon="basil:refresh-outline" class="text-xl" />
            </Button>

            <Button
            v-ripple
            outlined
            size="small"
            severity="secondary"
            class="flex-1 gap-x-2"
            @click="handleCancel(slotProps.data.id)"
            :loading="loading"
            :disabled="false"
            >
              <span class="text-xs">Cancel</span>
              <Icon icon="basil:close-outline" class="text-xl" />
            </Button>
          </template>
          <p v-else-if="!isEditingRow(slotProps.data.id)" class="px-4 text-sm text-gray-500">
            Cannot edit wastage from batch
          </p>
        </div>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
  <div class="px-4 py-2">
    <Button
      v-ripple
      outlined
      class="gap-x-2"
      @click="addWastage"
      :loading="loading"
      :disabled="isAddingNew"
    >
      <span> Add Wastage </span>
      <Icon icon="basil:plus-solid" class="text-2xl" />
    </Button>
  </div>
</template>
